import { useState, useCallback } from 'react'

/* ───────────────────────────────────────────────────────────────
 *  Pressable — iOS-style press feedback for any button/control.
 *
 *  Wrap any tappable element and it gets the native-feeling press:
 *  a quick scale-down on touch/press, then a spring-back on release.
 *  The curve is tuned to read as "responsive and solid" (what a pro
 *  iOS app feels like), NOT bouncy or rubbery.
 *
 *  Usage — as a drop-in around a button's CONTENTS:
 *
 *    <Pressable onPress={handleTap} style={glassCircleStyle} title="Close">
 *      <XMarkIcon />
 *    </Pressable>
 *
 *  It renders a <button> by default. Pass `as="div"` / `as="span"` to
 *  render something else (e.g. when nesting inside an existing button
 *  is illegal). It forwards style, className, disabled, title, etc.
 *
 *  CONFIG block — tune the feel in one place.
 * ─────────────────────────────────────────────────────────────── */
const PRESS_CONFIG = {
  scale: 0.92,                  // how far it squishes on press (0.90–0.94 is the iOS range)
  // Press DOWN is near-instant (fast, responsive). Release SPRINGS back.
  downTransition: 'transform 0.08s cubic-bezier(0.4, 0, 1, 1)',
  upTransition: 'transform 0.34s cubic-bezier(0.22, 1.2, 0.36, 1)',   // slight overshoot = lively, not bouncy
  // Disabled controls don't animate.
}

function Pressable({
  as: Tag = 'button',
  onPress,
  onClick,            // alias — some call sites pass onClick
  disabled = false,
  style = {},
  className,
  title,
  type,
  children,
  pressScale,         // optional per-instance override
  stopPropagation = false,
  ...rest
}) {
  const [pressed, setPressed] = useState(false)
  const scale = pressScale ?? PRESS_CONFIG.scale

  const down = useCallback(() => { if (!disabled) setPressed(true) }, [disabled])
  const up = useCallback(() => setPressed(false), [])

  const handleClick = useCallback((e) => {
    if (disabled) return
    if (stopPropagation) e.stopPropagation()
    onPress?.(e)
    onClick?.(e)
  }, [disabled, stopPropagation, onPress, onClick])

  const mergedStyle = {
    ...style,
    transform: `${style.transform ? style.transform + ' ' : ''}scale(${pressed ? scale : 1})`,
    transition: [
      style.transition,
      pressed ? PRESS_CONFIG.downTransition : PRESS_CONFIG.upTransition,
    ].filter(Boolean).join(', '),
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
  }

  const tagProps = Tag === 'button' ? { type: type || 'button', disabled } : {}

  return (
    <Tag
      {...tagProps}
      {...rest}
      className={className}
      title={title}
      style={mergedStyle}
      onClick={handleClick}
      onPointerDown={down}
      onPointerUp={up}
      onPointerLeave={up}
      onPointerCancel={up}
      onTouchStart={down}
      onTouchEnd={up}
    >
      {children}
    </Tag>
  )
}

export default Pressable